import type { UIMessage } from "ai";
import clsx from "clsx";
import { motion, useReducedMotion } from "framer-motion";
import { useState } from "react";
import Markdown from "react-markdown";

import type { QuizData, QuizQuestion } from "./ChatQuiz";
import ChatQuizCard from "./ChatQuizCard";
import ChatQuizResultCard from "./ChatQuizResultCard";
import ChatContentLink from "~/components/Chat/ChatContentLink";
import fetcher from "~/utils/fetcher.client";
import { parseContentHref } from "~/utils/parseContentHref";

const extractQuizId = (output: any): number | null => {
	const candidates = [
		output,
		output?.result,
		output?.quiz?.id,
		output?.result?.quiz?.id,
		output?.id,
	];

	for (const candidate of candidates) {
		if (typeof candidate === "number" && Number.isInteger(candidate)) {
			return candidate;
		}
		if (typeof candidate === "string" && /^\d+$/.test(candidate)) {
			return Number(candidate);
		}
	}

	return null;
};

const QUIZ_TOOL_NAME = "generate_practice_quiz";

// Live AI SDK v5/v6 parts embed the tool name in `type` ("tool-<name>") and
// have no top-level `toolName`; reloaded history uses the legacy
// `toolInvocation.toolName` shape. Check both.
const getToolName = (part: any): string | undefined => {
	if (typeof part?.toolInvocation?.toolName === "string") {
		return part.toolInvocation.toolName;
	}
	if (typeof part?.toolName === "string") return part.toolName;
	if (typeof part?.type === "string" && part.type.startsWith("tool-")) {
		return part.type.slice("tool-".length);
	}
	return undefined;
};

const QUIZ_LOADING_STATES = [
	"call",
	"input-streaming",
	"input-available",
	"partial-call",
];

const ChatMessage = ({ message }: { message: UIMessage }) => {
	const reduceMotion = useReducedMotion();
	const [startingKey, setStartingKey] = useState<string | null>(null);
	const isUser = message.role === "user";
	const text = message.parts
		.filter((part) => part.type === "text")
		.map((part) => (part.type === "text" ? part.text : ""))
		.join("");

	// Extract tool invocations: in-flight calls render a spinner card,
	// settled calls only render if they actually produced a quiz id.
	const quizToolInvocations = message.parts.filter((part: any) => {
		if (getToolName(part) !== QUIZ_TOOL_NAME) return false;

		const state = part.state ?? part.toolInvocation?.state ?? "call";
		if (QUIZ_LOADING_STATES.includes(state)) return true;

		return (
			extractQuizId(
				part.output ?? part.result ?? part.toolInvocation?.result,
			) !== null
		);
	});

	const handleStartQuiz = async (toolCall: any) => {
		const key = toolCall.toolInvocationId || toolCall.toolCallId;
		if (startingKey !== null) return;

		const outputData =
			toolCall.output ||
			toolCall.result ||
			toolCall.toolInvocation?.output ||
			toolCall.toolInvocation?.result;

		const quizId = extractQuizId(outputData);

		if (!quizId) {
			console.error("Quiz id not available in tool output", { outputData });
			return;
		}

		setStartingKey(key);
		try {
			const quiz = await fetcher<{ questions: QuizQuestion[] }>(
				`/quiz/${quizId}`,
				{ api: "chat" },
			);

			if (!quiz.questions || quiz.questions.length === 0) {
				console.error("Quiz has no questions yet", { quizId, quiz });
				return;
			}

			const quizData: QuizData = { quizId, questions: quiz.questions };

			window.dispatchEvent(
				new CustomEvent("open-chat-quiz", {
					detail: {
						quizData,
					},
				}),
			);
		} catch (err) {
			console.error(`Failed to load quiz ${quizId}`, err);
		} finally {
			setStartingKey(null);
		}
	};

	const quizResult =
		message.metadata &&
		typeof message.metadata === "object" &&
		"type" in message.metadata &&
		message.metadata.type === "quizResult"
			? (message.metadata as any)
			: null;

	return (
		<motion.div
			initial={reduceMotion ? false : { opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.25, ease: "easeOut" }}
			className={clsx(
				"flex flex-col gap-2",
				isUser ? "items-end" : "items-start",
			)}
		>
			{/* Render Text Content */}
			{text && !quizResult && (
				<div
					className={clsx("text-sm", {
						"max-w-[85%] rounded-2xl bg-slate-100 px-3.5 py-2.5 dark:bg-slate-800":
							isUser,
					})}
				>
					{isUser ? (
						<span className="wrap-break-word whitespace-pre-wrap">{text}</span>
					) : (
						<div className="prose-sm prose wrap-break-word dark:prose-invert max-w-none">
							<Markdown
								components={{
									a: ({ node: _node, children, href, ...props }) => {
										const parsed = parseContentHref(href);
										if (parsed && href) {
											const label =
												typeof children === "string"
													? children
													: String(children ?? "");
											return (
												<ChatContentLink
													type={parsed.type}
													slug={parsed.slug}
													href={href}
													label={label}
												/>
											);
										}
										return (
											<a
												{...props}
												href={href}
												target="_blank"
												rel="noopener noreferrer"
												className="text-sky-600 underline dark:text-sky-400"
											>
												{children}
											</a>
										);
									},
								}}
							>
								{text}
							</Markdown>
						</div>
					)}
				</div>
			)}

			{/* Render Tool Invocations */}
			{!isUser &&
				quizToolInvocations.map((toolCall: any) => {
					// Check state from toolInvocation.state (v4/v5 format) or part.state (v5 format)
					const state =
						toolCall.state || toolCall.toolInvocation?.state || "call";
					const isLoading = QUIZ_LOADING_STATES.includes(state);

					const key =
						toolCall.toolInvocationId ||
						toolCall.toolCallId ||
						toolCall.toolInvocation?.toolCallId;

					return (
						<ChatQuizCard
							key={key}
							isLoading={isLoading || startingKey === key}
							onStart={() => handleStartQuiz(toolCall)}
						/>
					);
				})}

			{/* Render Quiz Result */}
			{quizResult && (
				<ChatQuizResultCard
					score={quizResult.score}
					total={
						quizResult.score + (quizResult.incorrectQuestions?.length ?? 0)
					}
				/>
			)}
		</motion.div>
	);
};

export default ChatMessage;
